import debounce from 'lodash/debounce';
import React, { ForwardedRef, forwardRef, ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ThemeContext } from 'styled-components';

import axios, { AxiosResponse } from 'axios';
import { WithTranslation } from 'react-i18next';
import { CustomContent } from '../_DropList/DropList';
import clearResourceDataByName from '../../../actions/resource-fn/clearResourceDataByName';
import requestResourceByName from '../../../actions/resource-fn/requestResourceByName';
import useScopedSelector from '../../../hooks/useScopedSelector';
import excludePropsByComponent from '../../../utils/components/_excludePropsByComponent';
import filterPropsByComponent from '../../../utils/components/_filterPropsByComponent';
import resolveStringValue from '../../../utils/components/_resolveStringValue';
import Loader from '../../loader/Loader';
import FormErrorMessage from '../../message/FormErrorMessage';
import Resource, { ResourceProps } from '../../resource/Resource';
import SchemaContext from '../../schema/_SchemaContext';
import AutoSuggestInput, { AutoSuggestInputProps } from '../AutoSuggestInput';
import { StyledErrorIcon, StyledErrorMessage, StyledNoDataMessage } from './AutoSuggestResourceInput.styles';

export interface AutoSuggestResourceInputProps<Value, Data> extends
  Omit<AutoSuggestInputProps<Value>, 'name' | 'value' | 'emptyValue' | 'options'>,
  Omit<ResourceProps<Data>, 'url' | 'children'> {
  /** The url of the request. */
  url: string | ((search: string) => string);
  /** Name of the search param */
  searchParam?: string;
  /** The duration without changes to wait until the search request is sent. */
  debounceDuration?: number;
  /** Addtional error to display under the input. */
  additionalError?: AxiosResponse | Error | null;
  /** Array of options that overrides the options for the resource. */
  optionsOverride?: AutoSuggestInputProps<any>['options'];
  /** @ignore */
  minSearchLength?: AutoSuggestInputProps<Value>['minSearchLength'];
  /** @ignore */
  emptyValue?: string;
  /** @ignore */
  value?: Value;
}

interface AutoSuggestResourceInputInternalProps extends WithTranslation {
  autoSuggestId: number;
}

interface AutoSuggestResourceInputComposedProps<Value, Data> extends AutoSuggestResourceInputProps<Value, Data>, AutoSuggestResourceInputInternalProps {

}

const AutoSuggestResourceInputDefaultProps = {
  minSearchLength: 3,
  searchParam: 'search',
  emptyValue: '',
  debounceDuration: 300,
};

/**
 * @mode developer
 */
const AutoSuggestResourceInput = forwardRef(<Value, Data>({
  t, name, children, tReady, customContent, url, autoSuggestId, searchParam, value, minSearchLength, emptyValue, additionalError, debounceDuration, optionsOverride, onValueChange, onSearch, onOpened, ...props
}: AutoSuggestResourceInputComposedProps<Value, Data>, ref: ForwardedRef<HTMLInputElement>) => {
  const theme = useContext(ThemeContext);
  const [isOpened, setIsOpened] = useState(false);
  const [isOpening, setIsOpening] = useState(false);
  const dispatch = useDispatch();
  const [search, setSearch] = useState<string>((typeof value !== 'undefined') ? value as string : emptyValue);

  const ErrorIcon = useMemo(() => theme?.icons?.error, [theme]);
  const { schema } = useContext(SchemaContext);
  const resolvedName = name || `autoSuggest${autoSuggestId}`;
  const resourceName = useMemo(() => (
    schema.extendName('resource', { name: resolvedName })
  ), [schema, resolvedName]);

  const resourceDataDst = schema.getDataDst({ ...props, name: resourceName });

  const options: AutoSuggestInputProps<Value>['options'] = useScopedSelector(resourceDataDst);
  const pending: boolean = useScopedSelector(`${resourceName}.pending`);
  const error: AxiosResponse = useScopedSelector(`${resourceName}.error`);

  const resolvedOptions = useMemo(() => {
    if (optionsOverride) return optionsOverride;
    return (
      (options && search?.length >= minSearchLength && !isOpening && !error) ?
        options :
        []
    );
  }, [error, isOpening, minSearchLength, options, optionsOverride, search?.length]);

  const uniqueOptions = useMemo(() => {
    const map = new Map();
    resolvedOptions.forEach((option) => {
      map.set(resolveStringValue(option.value, option.key), option);
    });
    return Array.from(map.values());
  }, [resolvedOptions]);

  const getUrlWithOrigin = useCallback((stringUrl: string) => {
    if (stringUrl.startsWith?.('http')) {
      return stringUrl;
    }

    if (stringUrl.startsWith?.('/')) {
      return `${axios.defaults.baseURL || window.location.origin}${stringUrl}`;
    }

    return `${axios.defaults.baseURL || window.location.origin}/${stringUrl}`;
  }, []);

  const urlWithSearch = useMemo(() => {
    if (typeof url === 'function') {
      return url(search);
    }

    const newUrl = new URL(getUrlWithOrigin(url));

    newUrl.searchParams.set(searchParam, search);

    return newUrl.toString();
  }, [url, getUrlWithOrigin, searchParam, search]);

  const errorMessage = useMemo(() => {
    if (error || additionalError) {
      const currentError = (error || additionalError);
      return `${t(('status' in currentError && currentError?.status === 0) ? 'networkError' : 'serverError')}${t('tryAgain')}`;
    }
    return '';
  }, [additionalError, error, t]);

  const newCustomContent: CustomContent = useMemo(() => {
    if (error) {
      return [
        <StyledErrorMessage key="error" role="status">
          <StyledErrorIcon as={ErrorIcon} />
          {errorMessage}
        </StyledErrorMessage>,
        customContent,
      ];
    }

    if (options?.length === 0 && !isOpening) {
      return [
        <StyledNoDataMessage key="message" role="status">{t('noResultMessage')}</StyledNoDataMessage>,
        customContent,
      ];
    }

    return customContent;
  }, [ErrorIcon, customContent, error, errorMessage, isOpening, options?.length, t]);

  const handleValueChange = useCallback((newValue: Value | string) => {
    dispatch(clearResourceDataByName(resourceName));
    if (onValueChange) onValueChange(newValue);
  }, [dispatch, onValueChange, resourceName]);

  const handleSearch = useCallback((newSearch: string) => {
    setSearch(newSearch);
    if (onSearch) onSearch(newSearch);
  }, [onSearch]);

  const debounceUpdate = useMemo(() => (
    debounce(() => {
      dispatch(requestResourceByName(resourceName));
    }, debounceDuration)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [debounceDuration, dispatch, resourceName]);

  const handleOpened = useCallback((newIsOpened: boolean) => {
    setIsOpened(newIsOpened);

    if (newIsOpened) {
      setIsOpening(true);
    } else {
      setIsOpening(false);
    }

    if (onOpened) onOpened(newIsOpened);
  }, [onOpened]);

  useEffect(() => {
    if (isOpened && search.length >= minSearchLength && !optionsOverride) {
      debounceUpdate();
    }
  }, [debounceUpdate, isOpened, minSearchLength, optionsOverride, search, urlWithSearch]);

  useEffect(() => {
    if (isOpening && pending === false) {
      setIsOpening(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending]);

  return (
    <>
      <Resource<Data>
        {...filterPropsByComponent(props, Resource<Data>)}
        name={resourceName}
        url={urlWithSearch}
        dataDst={resourceDataDst}
      />
      <AutoSuggestInput<Value>
        {...excludePropsByComponent(props, Resource)}
        name={name}
        emptyValue={emptyValue}
        value={value}
        minSearchLength={optionsOverride ? 0 : minSearchLength}
        options={uniqueOptions}
        filterSearch={Boolean(optionsOverride)}
        rightSign={pending && <Loader size={20} />}
        customContent={newCustomContent}
        busy={pending}
        ref={ref}
        onSearch={handleSearch}
        onOpened={handleOpened}
        onValueChange={handleValueChange}
      />
      {additionalError && <FormErrorMessage>{errorMessage}</FormErrorMessage>}
    </>
  );
}) as (<Value, Data>(props: AutoSuggestResourceInputComposedProps<Value, Data>) => ReactElement<AutoSuggestResourceInputComposedProps<Value, Data>>) & {
  defaultProps: Partial<AutoSuggestResourceInputComposedProps<any, any>>;
};

AutoSuggestResourceInput.defaultProps = AutoSuggestResourceInputDefaultProps;

export default AutoSuggestResourceInput;
